
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ApproveWorksheetDialog } from "./ApproveWorksheetDialog";
import { Edit } from "lucide-react";
import { TooltipProvider } from "@/components/ui/tooltip";

interface WorksheetCheckStatusCellProps {
  reportId: string;
  normalizedStatus: "WAITING" | "APPROVED" | "REJECTED";
  mapStatusToColor: Record<string, string>;
  mapStatusToLabel: Record<string, string>;
  loadingId: string | null;
  dialogOpenFor: string | null;
  setDialogOpenFor: (id: string | null) => void;
  handleApprove: (status: "APPROVED" | "REJECTED", note: string) => Promise<void>;
  approved_at?: string;
  approved_by?: string;
  approved_note?: string;
}

export const WorksheetCheckStatusCell: React.FC<WorksheetCheckStatusCellProps> = ({
  reportId,
  normalizedStatus,
  mapStatusToColor,
  mapStatusToLabel,
  loadingId,
  dialogOpenFor,
  setDialogOpenFor,
  handleApprove,
  approved_at,
  approved_by,
  approved_note
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
    setDialogOpenFor(reportId);
  };

  if (normalizedStatus === "WAITING") {
    return (
      <div className="flex items-center gap-2">
        <span className={mapStatusToColor["WAITING"] + " font-medium"}>
          {mapStatusToLabel["WAITING"]}
        </span>
        <Button
          variant="default"
          size="sm"
          onClick={() => setDialogOpenFor(reportId)}
          disabled={!!loadingId}
        >
          ตรวจสอบ/Approve
        </Button>
        <ApproveWorksheetDialog
          open={dialogOpenFor === reportId}
          onOpenChange={(open) => setDialogOpenFor(open ? reportId : null)}
          onSubmit={async (status, note) => await handleApprove(status, note)}
          loading={loadingId === reportId}
          initialStatus={normalizedStatus}
          initialNote={approved_note}
        />
      </div>
    );
  }

  // Already APPROVED or REJECTED with edit option
  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={`${mapStatusToColor[normalizedStatus]} font-medium p-0 h-auto hover:bg-transparent`}
            >
              {mapStatusToLabel[normalizedStatus]}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">รายละเอียด</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleEditClick}
                  className="flex items-center gap-1"
                >
                  <Edit className="h-4 w-4" /> แก้ไข
                </Button>
              </div>
              <div className="text-sm">
                <p>
                  <span className="font-semibold">สถานะ:</span> {mapStatusToLabel[normalizedStatus]}
                </p>
                {approved_at && (
                  <p>
                    <span className="font-semibold">เวลา:</span> {new Date(approved_at).toLocaleString('th-TH')}
                  </p>
                )}
                {approved_by && (
                  <p>
                    <span className="font-semibold">ผู้ดำเนินการ:</span> {approved_by}
                  </p>
                )}
                <p>
                  <span className="font-semibold">หมายเหตุ:</span> {approved_note || "-"}
                </p>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        
        <ApproveWorksheetDialog
          open={dialogOpenFor === reportId && isEditing}
          onOpenChange={(open) => {
            if (!open) {
              setDialogOpenFor(null);
              setIsEditing(false);
            }
          }}
          onSubmit={async (status, note) => {
            await handleApprove(status, note);
            setIsEditing(false);
          }}
          loading={loadingId === reportId}
          initialStatus={normalizedStatus}
          initialNote={approved_note}
          isEditing={true}
        />
      </div>
    </TooltipProvider>
  );
};
